import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, Pencil, Plus } from 'lucide-react';
import { leadService, proposalService } from '../../../services/erpApi';
import './ProspectingPage.css';

interface ProposalItem {
  id: number;
  number: string;
  status: string;
  total_amount?: number;
  discount_amount?: number;
  discount_percent?: number;
  created_at?: string;
  updated_at?: string;
}

interface LeadInfo {
  id: number;
  name: string;
  institution?: string;
  email?: string;
}

const LeadProposalsPage: React.FC = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [lead, setLead] = useState<LeadInfo | null>(null);
  const [proposals, setProposals] = useState<ProposalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    try {
      const [leadRes, proposalsRes] = await Promise.all([
        leadService.getById?.(leadId),
        proposalService.getByLeadId(leadId)
      ]);

      if (leadRes?.success) setLead(leadRes.data);
      if (proposalsRes?.success) setProposals(proposalsRes.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar propostas do lead:', err);
      setError('Não foi possível carregar as propostas deste lead.');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const leadName = useMemo(() => {
    if (lead?.name) return lead.name;
    if (searchParams.get('lead_name')) return searchParams.get('lead_name');
    return 'Lead';
  }, [lead, searchParams]);

  const handleNewProposal = () => {
    if (!leadId) return;
    navigate(`/erp/prospecting/proposals/new?lead_id=${leadId}`);
  };

  const handleEdit = (proposalId: number) => {
    if (!leadId) return;
    navigate(`/erp/prospecting/proposals/new?lead_id=${leadId}&proposal_id=${proposalId}`);
  };

  const formatCurrency = (val?: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

  const formatDate = (val?: string) => {
    if (!val) return '--';
    const d = new Date(val);
    return d.toLocaleDateString('pt-BR');
  };

  return (
    <div className="prospecting-page">
      <div className="prospecting-header">
        <div>
          <button className="proposal-back-button" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} /> Voltar
          </button>
          <h1 className="prospecting-title" style={{ marginTop: '1rem' }}>
            Propostas de {leadName}
          </h1>
          {lead?.institution && (
            <p className="prospecting-subtitle">{lead.institution}</p>
          )}
        </div>
        <div className="prospecting-header-actions">
          <button className="prospecting-btn prospecting-btn-primary" onClick={handleNewProposal}>
            <Plus size={16} /> Nova Proposta
          </button>
        </div>
      </div>

      {loading ? (
        <div className="prospecting-loading-state">
          <Loader2 className="animate-spin" size={24} />
          <p>Carregando propostas...</p>
        </div>
      ) : error ? (
        <div className="prospecting-empty-state">
          <p>{error}</p>
        </div>
      ) : proposals.length === 0 ? (
        <div className="prospecting-empty-state">
          <FileText size={32} />
          <p>Nenhuma proposta para este lead ainda.</p>
          <button className="prospecting-btn prospecting-btn-primary" onClick={handleNewProposal}>
            Criar primeira proposta
          </button>
        </div>
      ) : (
        <div className="prospecting-leads-grid">
          {proposals.map((proposal) => (
            <div key={proposal.id} className="prospecting-lead-card">
              <div className="prospecting-lead-header">
                <div>
                  <p className="prospecting-lead-name">Proposta {proposal.number}</p>
                  <p className="prospecting-lead-meta">
                    Status: <strong>{proposal.status || 'draft'}</strong>
                  </p>
                </div>
              </div>
              <div className="prospecting-lead-body">
                <p className="prospecting-lead-meta">
                  Emitida em: {formatDate(proposal.created_at)}
                </p>
                <p className="prospecting-lead-meta">
                  Atualizada em: {formatDate(proposal.updated_at)}
                </p>
                <p className="prospecting-lead-meta">
                  Total: <strong>{formatCurrency(proposal.total_amount)}</strong>
                </p>
                {proposal.discount_percent ? (
                  <p className="prospecting-lead-meta">
                    Desconto: {proposal.discount_percent}% ({formatCurrency(proposal.discount_amount)})
                  </p>
                ) : null}
              </div>
              <div className="prospecting-lead-actions">
                <button
                  className="prospecting-lead-action-btn"
                  title="Editar proposta"
                  onClick={() => handleEdit(proposal.id)}
                >
                  <Pencil size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadProposalsPage;
